<template>
  <button
    v-bind="rootBindings"
    @click.prevent="startSession"
  >
    <ApplePayIcon />
  </button>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { i18n } from '@/i18n';
import { DEFAULT_LOCALE } from '../../../services/language-helpers';
import { saveToken } from '../token/token-service';
import { checkRequiredFields } from '../validation/validator';
import { buildBillingData } from '../utils/billing-contact';
import { formatDecimalAmount } from '../../../utilities/formatDecimalAmount';
import { COUNTRY_CODE_US } from '../constants';
import * as ApplePayApi from '../api/apple-pay-api';
import type {
  ClientContextIdHeader,
  ApplePaySettings,
  ApplePayPayPayload,
  ApplePayErrorPayload,
} from '../types';
import ApplePayIcon from '../assets/apple-pay.svg';
const defaultApplePaySettings = {
  supportedNetworks: ['visa', 'masterCard'],
  merchantCapabilities: ['supports3DS'],
  label: 'Paybis',
  version: 3,
} satisfies ApplePaySettings;

const props = withDefaults(
  defineProps<{
    amount: string | number;
    currency: string;
    countryCode?: string | null;
    invoice?: string;
    hasError?: boolean;
    clientContextIdHeader: ClientContextIdHeader;
    /** When true: store token and emit payment-authorized. When false (default): emit pay with payload for parent to complete. */
    oneClick?: boolean;
    /** Optional settings for session (used when oneClick=false or to override defaults). */
    settings?: ApplePaySettings;
    disabled?: boolean;
    loading?: boolean;
  }>(),
  {
    oneClick: false,
    disabled: false,
    loading: false,
  },
);

const emit = defineEmits<{
  (event: 'payment-authorized'): void;
  (event: 'pay', payload: ApplePayPayPayload): void;
  (event: 'error', payload: ApplePayErrorPayload): void;
  (event: 'cancel'): void;
  (event: 'click'): void;
}>();

const attrs = useAttrs();
const isDisabled = computed(() => props.disabled || props.loading);

const rootClass = computed(() => [
  attrs.class,
  'apple-pay-button-with-text',
  'btn',
  'btn-primary',
  'btn-lg',
  props.loading && 'apple-pay-button-with-text--loading',
].filter(Boolean));

const rootBindings = computed(() => ({
  ...attrs,
  class: rootClass.value,
  type: 'button' as const,
  lang: ((i18n.locale as string | undefined) && i18n.locale !== 'none' ? i18n.locale : DEFAULT_LOCALE),
  'aria-label': (attrs['aria-label'] as string) ?? 'Apple Pay',
  disabled: isDisabled.value || undefined,
}));

let isSessionActive = false;

function emitError(error: unknown, session: ApplePaySession | null = null) {
  let normalizedError = new Error('Unknown Apple Pay error');

  if (error instanceof Error) {
    normalizedError = error;
  } else if (typeof error === 'string') {
    normalizedError = new Error(error);
  }

  emit('error', {
    error: normalizedError,
    session,
  });
}

function startSession() {
  if (props.disabled || props.loading || props.hasError || isSessionActive) return;

  if (typeof ApplePaySession === 'undefined') {
    emitError(new Error('Apple Pay is not supported in this browser.'));
    return;
  }

  if (!ApplePaySession.canMakePayments()) {
    emitError(new Error('Apple Pay is not available for this user.'));
    return;
  }

  const numeric = Number(props.amount);

  if (!Number.isFinite(numeric) || numeric <= 0) {
    emitError(new Error('Amount is required for Apple Pay Payment.'));
    return;
  }

  const formattedAmount = formatDecimalAmount(props.amount);

  if (props.settings && !props.settings.paymentLabel && !props.settings.label) {
    emitError(new Error('Apple Pay settings must include label or paymentLabel.'));
    return;
  }

  const settings = {
    ...defaultApplePaySettings,
    ...props.settings,
    label:
      props.settings?.paymentLabel ??
      props.settings?.label ??
      defaultApplePaySettings.label,
  };

  if (!ApplePaySession.supportsVersion(settings.version)) {
    emitError(new Error('Unsupported Apple Pay version.'));
    return;
  }

  // In regular flow parent starts processing/locking on click before Apple Pay callbacks.
  if (!props.oneClick) {
    emit('click');
  }

  const session = new ApplePaySession(settings.version, {
    countryCode: props.countryCode || COUNTRY_CODE_US,
    currencyCode: props.currency,
    requiredBillingContactFields: ['postalAddress'],
    // Spread into fresh plain arrays: settings may come from reactive Vuex
    // state, and the SDK postMessage()s this request, which throws
    // DataCloneError on reactive arrays. See PD apple-pay DataCloneError fix.
    supportedNetworks: [...settings.supportedNetworks],
    merchantCapabilities: [...settings.merchantCapabilities],
    total: {
      label: settings.label,
      amount: formattedAmount,
    },
  });

  isSessionActive = true;

  session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
    try {
      const response = await ApplePayApi.validatePaymentSession(
        {
          validationUrl: event.validationURL,
          invoice: props.invoice,
        },
        props.clientContextIdHeader,
      );
      const data = response.data?.data ?? response.data;
      session.completeMerchantValidation(data);
    } catch (error) {
      emitError(error, session);
      // Aborting an already-closed session (e.g. after a cancelled/superseded
      // validation request) throws InvalidAccessError — swallow it so it does
      // not surface as a noisy, misleading Sentry error.
      try {
        session?.abort();
      } catch {
        // Session already closed — nothing to abort.
      }
      isSessionActive = false;
    }
  };

  const updateParams = {
    newTotal: {
      amount: formattedAmount,
      currency: props.currency,
      label: settings.label,
    },
  };

  session.onpaymentmethodselected = () => {
    session.completePaymentMethodSelection(updateParams);
  };

  session.onshippingmethodselected = () => {
    session.completeShippingMethodSelection(updateParams);
  };

  session.onshippingcontactselected = () => {
    session.completeShippingContactSelection(updateParams);
  };

  session.onpaymentauthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
    const { token, billingContact } = event.payment;

    const validationErrors = checkRequiredFields(billingContact || {});
    if (validationErrors) {
      const applePayErrors = validationErrors.map(
        (err: { contactField: string; message: string }) =>
          new ApplePayError(
            'billingContactInvalid',
            err.contactField as ApplePayJS.ApplePayErrorContactField,
            err.message,
          ),
      );

      session.completePayment({
        status: ApplePaySession.STATUS_FAILURE,
        errors: applePayErrors,
      });

      isSessionActive = false;
      return;
    }

    const { cardholderName, billingAddress } = buildBillingData(billingContact);

    if (props.oneClick) {
      try {
        const response = await ApplePayApi.storeApplePayToken(token.paymentData);
        const data = response?.data || response;

        const tokenData = {
          tokenId: data?.token_id,
          expiresAt: data?.expires_at,
          timestamp: Date.now(),
        };

        const [clientContextId] = Object.values(props.clientContextIdHeader);

        saveToken({
          ...tokenData,
          ...billingAddress,
          cardholderName,
          clientContextId,
        });

        session.completePayment(ApplePaySession.STATUS_SUCCESS);
        emit('payment-authorized');
      } catch (error) {
        emitError(error, session);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
      }
    } else {
      emit('pay', {
        paymentToken: token.paymentData,
        cardholderName,
        billingAddress,
        applePaySession: session,
        clientContextIdHeader: props.clientContextIdHeader,
      });
    }

    isSessionActive = false;
  };

  session.oncancel = () => {
    isSessionActive = false;
    emit('cancel');
  };

  try {
    session.begin();
  } catch (error) {
    isSessionActive = false;
    emitError(error, session);
  }
}
</script>

<style scoped lang="scss">
.apple-pay-button-with-text {
  cursor: pointer;
  display: flex;
  --apple-pay-scale: 1;
  position: relative;

  &__image {
    display: flex;
    height: 100%;
    align-items: center;

    .apple-pay-button-with-text--loading > & > img {
      display: none;
    }
  }

  &--loading {
    min-height: 48px;
    min-width: 110px;
    color: transparent;
    pointer-events: none;

    > svg {
      visibility: hidden;
    }

    &:after {
      content: '';
      position: absolute;
      top: calc(50% - 12px);
      left: calc(50% - 12px);
      background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAARjSURBVHgB7VnJihNRFD1JnOd5AIcGxQEVnBDEaeVCQUQQRRHEnT+gIILiUhFc+AmCC1Fw7c4RdaEiiAiiOE/ddkN3a9NTeW7Xu9arV69SqSQdI/SBw61UVd47576hKjfACEZQEwqoI4IgmMwwn1xByvFUc6mb/ER2FgqFR6gjajZA0ZMYdpM7yJXkFLJEFg3tY+Vj8gb5kIY+oAZUbYDC1zIcI9dniC1Z533Xr5MXqzWS2wCFT2Q4Su53RBY8QktIjoZ7r/IaeYFG3iMHchmg+DUMJ8h5KaJ9Yu3jciMh52QU9uQxUaz0Rorfy3CenKOnrKgctI71mpukghWLTmwhTyEHRlVyE8UfYjjsEatC9LMI6SSfkt/ILnN9GrnKcLo5p0aK1mfhDuRApgGKP8hwAPHsCkuWcNkmb5H3OPxPMtrbbNo7hGgGFC0jd5EDhYzOJGPnnA7cuXuTvELhXcgBtr2Q4SR5xGpL1sAutvWu0nYKZTqYzXCWnItoeEuWiVbyEjt7jhrAfhYhNNFBXmV7HXm+X87AcYTz0Z6jmikRf5qdfcc/hncNUPwshm2IL1Q1+4M80wziBWmLeB85gPiWp8eXm0W8IGHAZH8LwuzrFqnT5zbFv0ATwTcCyxFl36YYuY4mg8+A7NOafSAy8IDZb0WTwWdgKaIHlD0CuR4wjULMAOf/MoTiBxDNfWEPs/8KTQh3BMYhFC/QeS+o+MnYaLgGZiC+fcIc/zcGZATsBaz4hQaD0zn1LYHTWWdGwoD9mmzDd264oc8eF/pWPATXgGRap1BgnR+LxsM2oHoSP5B8BgKHgmloPEYjEis6ioh2yL9wDXx2blATi9B4jPLoSKwLd461k78RmlDKnBvDRdWCBoF9iXhfUaDIBRxbjzEDvNjD8BHRYhaqkWVoHGQ39JVoAvdG3yp/iXj21cRqZmbYFzP7EKET4C/B9Ln3+ww8Q1y4Ho8ht2L4ITVVu45k/xLsd29OGDDT6C2SJiSuNT/GhwVmhNWAWxzrdec/kF7YuoPIgDsSO00Vut7iZeHOhb/OKsfdvu95DZjS3mMkR0CizM999TTBtmTPX4Bw71fh9tSRt+EBVGrA4D7CUoddMtQoBd497Hg+ahcvbS1G+LR3a05iQoR3pn0/q7AlWdmP+LbqUn4nPKuisCWZliLxTESja/8W0fa/mnWZ34DpaB3C3ccWHSA5tV6Tb7IqFmxP/rWRV5MZiDLsa1tiG9trL9depgHT6SaGDUiOhPusEEq22g17zTXJtmzDU0yTvrVlG5D4g+J/ZmmryIAxsdGYGPCYCJB8/ajks68NiV+yMp/bgDEhi3Y7wp0o8IgYhP8p3o/kSPmSMPQqQ/HdqBC5DBgT8qee/D+2pIyBtHM+8ZoI+T/ha9p2WTcDjhH5y6kF/jWRNhruLtYm4im8E1WgagMKs49LKV728plIX+Aa5YVMxEqRTBZqP2pAzQZcmNqqvBaMR/QK3IdIeE+tokcwgjriD2/74mqBH4MoAAAAAElFTkSuQmCC');
      background-size: contain;
      background-position: center center;
      background-repeat: no-repeat;
      width: 24px;
      height: 24px;
      animation: spin 2s linear infinite;
    }
  }
}
</style>
